import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { machineDetailPath } from '../../utils/machineRoutes';

const API_BASE = '/machinesim/api';

const EDIT_KEYS = new Set([
  'execution',
  'part_count',
  'feedrate',
  'program',
  'program_header',
  'blockDelete',
  'cycleMode',
]);

function siteLabel() {
  return import.meta.env.VITE_SITE_NAME || 'Chentronix';
}

function operatorHref(machineName) {
  const base = import.meta.env.VITE_OPERATOR_MACHINE_BASE;
  if (base) {
    const b = base.replace(/\/$/, '');
    return `${b}/${encodeURIComponent(machineName)}`;
  }
  return machineDetailPath('/chentronix', machineName);
}

export default function StreamMachineDetail() {
  const { machineName: machineNameParam } = useParams();
  const machineName = useMemo(() => decodeURIComponent(machineNameParam || ''), [machineNameParam]);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  /** Draft values for editable fields */
  const [draft, setDraft] = useState({
    execution: 'READY',
    part_count: 0,
    feedrate: 100,
    program: '',
    program_header: '',
    blockDelete: false,
    cycleMode: 'AUTO',
  });

  const load = useCallback(async () => {
    if (!machineName) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/${encodeURIComponent(machineName)}/status`);
      const data = res.data;
      setStatus(data);
      setDraft({
        execution: data.execution ?? 'READY',
        part_count: data.part_count ?? 0,
        feedrate: data.feedrate ?? 100,
        program: data.program ?? '',
        program_header: data.program_header ?? '',
        blockDelete: !!data.blockDelete,
        cycleMode: data.cycleMode ?? 'AUTO',
      });
    } catch (e) {
      setError(e.response?.status === 404 ? 'Machine not found' : e.message || 'Failed to load');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [machineName]);

  useEffect(() => {
    load();
  }, [load]);

  const readOnlyEntries = useMemo(() => {
    if (!status || typeof status !== 'object') return [];
    return Object.keys(status)
      .filter((k) => !EDIT_KEYS.has(k))
      .map((k) => {
        const v = status[k];
        let display;
        if (v !== null && typeof v === 'object') {
          display = JSON.stringify(v, null, 2);
        } else {
          display = v === undefined ? '' : String(v);
        }
        return { key: k, display };
      });
  }, [status]);

  const applyEdits = async () => {
    setSaving(true);
    setSaveMsg(null);
    const path = (p) => `${API_BASE}/${encodeURIComponent(machineName)}${p}`;
    try {
      await axios.post(path('/set-execution'), { state: draft.execution });
      await axios.post(path('/part-count'), { partCount: Number(draft.part_count) });
      await axios.post(path('/set-feedrate'), { feedrate: Number(draft.feedrate) });
      await axios.post(path('/set-program'), { program: draft.program });
      await axios.post(path('/set-program-header'), { header: draft.program_header });
      await axios.post(path('/set-block-delete'), { blockDelete: draft.blockDelete });
      await axios.post(path('/set-cycle-mode'), { cycleMode: draft.cycleMode });
      setSaveMsg('Saved.');
      await load();
    } catch (e) {
      setSaveMsg(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-24 text-foreground">
        <p>Loading…</p>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 text-destructive px-4 py-16">
        <p>{error || 'Unknown error'}</p>
        <Link to=".." className="text-primary underline">
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className="text-foreground p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <Link to=".." className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-block">
              ← Machines
            </Link>
            <h1 className="text-2xl font-bold font-mono mt-1">{machineName}</h1>
            <p className="text-xs text-muted-foreground mt-1 font-mono">
              UNS: {siteLabel()}/{machineName}/state/&hellip;
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={operatorHref(machineName)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm text-primary hover:bg-accent"
              title="Open full operator UI (Chentronix simulator)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              Operator UI
            </a>
          </div>
        </div>

        <section className="mb-8 rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Edit fields</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">execution</span>
              <select
                className="bg-background border border-border rounded px-3 py-2 text-foreground"
                value={draft.execution}
                onChange={(e) => setDraft((d) => ({ ...d, execution: e.target.value }))}
              >
                <option value="READY">READY</option>
                <option value="ACTIVE">ACTIVE</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">part_count</span>
              <input
                type="number"
                min={0}
                className="bg-background border border-border rounded px-3 py-2 font-mono"
                value={draft.part_count}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, part_count: Number(e.target.value) }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">feedrate</span>
              <input
                type="number"
                className="bg-background border border-border rounded px-3 py-2 font-mono"
                value={draft.feedrate}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, feedrate: Number(e.target.value) }))
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">cycleMode</span>
              <select
                className="bg-background border border-border rounded px-3 py-2"
                value={draft.cycleMode}
                onChange={(e) => setDraft((d) => ({ ...d, cycleMode: e.target.value }))}
              >
                <option value="AUTO">AUTO</option>
                <option value="SINGLE">SINGLE</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-xs text-muted-foreground">program</span>
              <input
                type="text"
                className="bg-background border border-border rounded px-3 py-2 font-mono text-sm"
                value={draft.program}
                onChange={(e) => setDraft((d) => ({ ...d, program: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-xs text-muted-foreground">program_header</span>
              <input
                type="text"
                className="bg-background border border-border rounded px-3 py-2 font-mono text-sm"
                value={draft.program_header}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, program_header: e.target.value }))
                }
              />
            </label>
            <label className="flex items-center gap-2 sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.blockDelete}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, blockDelete: e.target.checked }))
                }
              />
              <span className="text-sm text-foreground">blockDelete</span>
            </label>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={saving}
              onClick={applyEdits}
              className="px-4 py-2 rounded-lg bg-primary hover:opacity-90 text-primary-foreground disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Apply changes'}
            </button>
            {saveMsg && (
              <span className={`text-sm ${saveMsg === 'Saved.' ? 'text-cn-success' : 'text-cn-warning'}`}>
                {saveMsg}
              </span>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground mb-3">Read-only (same API state)</h2>
          <p className="text-xs text-muted-foreground mb-4">
            These keys are emitted as UNS leaves where applicable; there is no single REST setter for each yet.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-mono">
              <tbody>
                {readOnlyEntries.map(({ key, display }) => (
                  <tr key={key} className="border-t border-border">
                    <td className="py-2 pr-4 align-top text-muted-foreground whitespace-nowrap">{key}</td>
                    <td className="py-2 text-foreground whitespace-pre-wrap break-all">{display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
